/**
 * ElementsPanel.tsx
 * -----------------
 * Tools tab — displays all insertable elements in a flat 2-column grid.
 * Clicking a card inserts the element at the cursor position.
 */

import * as React from "react";
import { Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import * as FluentIcons from "@fluentui/react-icons";
import type { ElementDefinition } from "../elements";
import { elementRegistry } from "../elements";
// useActiveTemplate is not required here; insertions go through useInsertElement
import { useInsertElement } from './hooks/useInsertElement';
import { useTemplateStore } from './store/templateStore';

type IconComponent = React.ComponentType<{ fontSize?: number }>;

const DEFAULT_ICON_NAME = "TextBulletListSquareRegular";

function renderElementIcon(iconName: string): React.ReactNode {
  const iconMap = FluentIcons as unknown as Record<string, IconComponent>;
  const Icon = iconMap[iconName] ?? iconMap[DEFAULT_ICON_NAME];
  return Icon ? <Icon fontSize={22} /> : null;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  hintText: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
    marginBottom: "4px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    padding: "14px 8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    minHeight: "88px",
    transition: "border-color 0.15s, box-shadow 0.15s",
    ":hover": {
      boxShadow: tokens.shadow4,
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  cardIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: "22px",
  },
  cardLabel: {
    fontWeight: "600",
    fontSize: "12px",
    color: tokens.colorNeutralForeground1,
    textAlign: "center",
  },
  cardDesc: {
    fontSize: "10px",
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    lineHeight: "1.3",
  },
  statusArea: {
    minHeight: "32px",
  },
});

const EXCLUDED_TOOL_IDS = new Set([
  'full-exhibit',
  'half-exhibit',
  'double-exhibit',
  'half-with-text-exhibit',
  'first-page-exhibit',
  'table-box',
  'double-table-exhibit',
  'table-graph-exhibit',
]);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ElementsPanel: React.FC = () => {
  const styles = useStyles();
  const activeTemplateId = useTemplateStore((s) => s.activeTemplateId);
  const elements = React.useMemo(
    () =>
      elementRegistry.getAll().filter(
        (element) =>
          !EXCLUDED_TOOL_IDS.has(element.id) &&
          (!element.templateIds || element.templateIds.includes(activeTemplateId))
      ),
    [activeTemplateId]
  );

  const { insert, isInserting, insertingId } = useInsertElement();

  const handleInsert = async (element: ElementDefinition) => {
    // insert() is no-op if an insertion is already in progress (serialized by the hook)
    await insert(element.id);
  };

  return (
    <div className={styles.panel}>

      {/* ── Hint ────────────────────────────────────────────────────────── */}
      <Text className={styles.hintText}>
        Click an element to insert it at your cursor position.
      </Text>

      {/* ── All-tools grid (2 × N) ──────────────────────────────────────── */}
      <div className={styles.grid}>
        {elements.map((element) => {
          const isLoading = isInserting && insertingId === element.id;
          return (
            <button
              key={element.id}
              className={styles.card}
              onClick={() => handleInsert(element)}
              disabled={isLoading}
              aria-disabled={isLoading}
              title={element.description}
              aria-label={`Insert ${element.label}`}
            >
              {isLoading ? (
                <Spinner size="tiny" aria-label={`Inserting ${element.label}`} />
              ) : (
                <span className={styles.cardIcon}>{renderElementIcon(element.icon)}</span>
              )}
              <span className={styles.cardLabel}>{element.label}</span>
              <span className={styles.cardDesc}>{element.description}</span>
            </button>
          );
        })}
      </div>

      {/* Status area intentionally handled by global toasts */}
    </div>
  );
};

export default ElementsPanel;
